import { HorseRacingEvent } from "@virtuallysports/vs-utils";
import React, { createContext, useContext, useEffect, useState } from "react";
import { EventData } from "../EventData";
import { EulerStateManager } from "../EulerStateManager";
import { EulerStateEvents, GameStateEvents } from "../Consts";
import { StateManager } from "../GameStateManager";

interface DataProviderProps {
  children: React.ReactNode;
}

export type CompetitorData = {
  id: string;
  name: string;
  cardinal: number;
  oddsWin: number;
  form: string;
  arrival: number;
};

const defaultState: HorseRacingEvent = {
  advertising: "",
  channel: "",
  competitionId: "",
  country: "",
  competitors: [],
  competitorsNum: "",
  completedTime: "",
  currentTime: "",
  duration: 0,
  gameTimeMultiplier: 0,
  id: "",
  language: "",
  locale: "",
  markets: {},
  marketsNum: 0,
  noMoreBets: "",
  readyTime: "",
  results: "N/A",
  resultsTime: "",
  scheduleName: "",
  sportType: "",
  startTime: new Date().toUTCString(),
  status: "ready",
  timezone: "",
};

const DataContext = createContext({
  eventData: defaultState,
  competitorsData: [] as CompetitorData[],
});

export default function DataProvider({ children }: DataProviderProps) {
  const [eventData, setEventData] = useState<HorseRacingEvent>(defaultState);
  const [competitorsData, setCompetitosData] = useState<CompetitorData[]>([]);

  function resetData() {
    setEventData(defaultState);
    setCompetitosData([]);
  }

  function setCompetitorsWithMarkets() {
    const eventData = EventData.getInstance();
    const data = eventData.getEventData();
    if (data) {
      setEventData(data);
    }
    const competitors = eventData.getCompetitors();
    const markets = eventData.getMarkets();
    const arrival = eventData.getArrivalOrderFull();
  
    const competitorData: CompetitorData[] = [];
    for (let i = 0; i < competitors.length; i++) {
      const comp = competitors[i].getCompetitor();
      const mainMarket = markets.find((market) => market.type === "101");
      const choice = mainMarket?.choices.find(
        (choice) => String(choice.refCompetitor) === comp.id,
      );
      
      // Calculate odds value - handle both price and probability properties
      let oddsWin = 0;
      if (choice) {
        if (choice.price !== undefined && choice.price !== null) {
          // Original format with direct price
          oddsWin = Number(choice.price);
        } else if (choice.probability !== undefined && choice.probability !== null) {
          // New format with probability that needs conversion to odds
          const probability = Number(choice.probability);
          if (!isNaN(probability) && probability > 0) {
            // Standard formula: odds = 1 / probability
            oddsWin = 1 / probability;
            // Round to 2 decimal places for display
            oddsWin = Math.round(oddsWin * 100) / 100;
          }
        }
      }
  
      const indexOf = arrival.findIndex((el) => el === comp.cardinal);
  
      competitorData.push({
        cardinal: comp.cardinal,
        id: comp.id,
        name: comp.name,
        oddsWin: oddsWin, // Use the calculated odds value
        form: comp.form,
        arrival: indexOf + 1,
      });
    }
    setCompetitosData(competitorData);
  }

  useEffect(() => {
    const eulerStateManager = EulerStateManager.getInstance();
    const gameStateManager = StateManager.getInstance();

    gameStateManager.subscribeToEvent(
      GameStateEvents.ON_ENTER_RACE_IN_PROGRESS_STATE,
      setCompetitorsWithMarkets,
    );

    eulerStateManager.subscribeToEvent(
      EulerStateEvents.ON_ENTER_LOAD_AND_SHOW_ODDS_STATE,
      setCompetitorsWithMarkets,
    );

    eulerStateManager.subscribeToEvent(
      // Should this be done in on get next event state or in on event end state?
      EulerStateEvents.ON_ENTER_GET_NEXT_EVENT_STATE,
      resetData,
    );

    return () => {
      gameStateManager.unsubscribeFromEvent(
        GameStateEvents.ON_ENTER_RACE_IN_PROGRESS_STATE,
        setCompetitorsWithMarkets,
      );

      eulerStateManager.unsubscribeFromEvent(
        EulerStateEvents.ON_ENTER_LOAD_AND_SHOW_ODDS_STATE,
        setCompetitorsWithMarkets,
      );

      eulerStateManager.unsubscribeFromEvent(
        EulerStateEvents.ON_ENTER_GET_NEXT_EVENT_STATE,
        resetData,
      );
    };
  }, []);

  return (
    <DataContext.Provider value={{ eventData, competitorsData }}>
      {children}
    </DataContext.Provider>
  );
}

export function useEventData() {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error("useData must be used within a DataProvider");
  }
  return context;
}
